import { format, isValid, parseISO } from "date-fns";

import {
  DATE_FORMAT,
  DAY_MONTH_FORMAT,
  DISPLAY_DATE_SLASH_FORMAT,
  DISPLAY_DATETIME_SLASH_FORMAT,
  DISPLAY_SHORT_DATE_FORMAT,
  MS_PER_MINUTE,
} from "./constants";
import { formatDate, isDateOnlyString, parseIsoDateLocal } from "./iso";

/**
 * FALLBACK CONVENTION: a display formatter given falsy/unparseable input returns
 * the em-dash placeholder {@link EM_DASH} so callers can render unconditionally.
 * (Wire/parse helpers in `iso.ts` / `wire.ts` return `""`/`undefined` instead so
 * the value is omitted rather than shown; `formatDuration` likewise uses the
 * em-dash for missing/invalid input, but `0m` for a real zero.) The one exception
 * is {@link formatDateWithOrdinal}, whose "Not specified" / "Invalid date" are
 * deliberate, richer domain messages.
 */
export const EM_DASH = "—";

/**
 * Shared body for the fixed (not org-driven) datetime formatters below: parse an
 * ISO string with the native `Date`, and render it with `fmt` or the em-dash for
 * falsy/invalid input. Not exported — the named formatters are the public surface.
 */
const formatFixedDateTime = (value: string | null | undefined, fmt: string): string => {
  if (!value) return EM_DASH;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? EM_DASH : format(date, fmt);
};

/**
 * Shared relative-time ladder: maps a whole-minute age to a short phrase
 * ("Just now", "5m ago", "3h ago", "2d ago"), or `null` for anything a week or
 * older so the caller can substitute an absolute date. Backs both
 * {@link formatTimeAgo} here and `formatRelativeTime` in the org-format module.
 */
export const relativeTimePhrase = (diffMinutes: number): string | null => {
  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const hours = Math.floor(diffMinutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return null;
};

/**
 * Formats a date-only `yyyy-MM-dd` string in the user's LOCAL timezone as the fixed
 * numeric UK date `dd/MM/yyyy`. Parses via {@link parseIsoDateLocal} to avoid the
 * `new Date("yyyy-MM-dd")` UTC-midnight parse that renders as the previous day west
 * of UTC. Returns the original string when it is not a valid date-only value.
 * Deliberately fixed (not org-driven) — for form-field / compact contexts.
 */
export const formatSlashDate = (dateString: string): string => {
  const parsed = parseIsoDateLocal(dateString);
  return parsed ? format(parsed, DISPLAY_DATE_SLASH_FORMAT) : dateString;
};

/**
 * Formats an ISO datetime string as the fixed numeric UK datetime `dd/MM/yyyy HH:mm`
 * for compact display contexts such as table columns. Returns em-dash for missing or
 * invalid values. Deliberately fixed (not org-driven) — distinct from the org-aware
 * `formatDateTime` in `@ethisyscore/core-utils/date/org-format`.
 */
export const formatCompactDateTime = (value?: string | null): string =>
  formatFixedDateTime(value, DISPLAY_DATETIME_SLASH_FORMAT);

/**
 * Formats an ISO date/datetime string as a short weekday date `EEE d MMM` (e.g. `Wed 5 Mar`).
 * Returns em-dash for missing or invalid values. Deliberately fixed (not org-driven).
 */
export const formatShortDate = (value?: string | null): string =>
  formatFixedDateTime(value, DISPLAY_SHORT_DATE_FORMAT);

/**
 * Formats an ISO date/datetime string as an abbreviated month-day `MMM d` (e.g. `Mar 5`).
 * Intended for chart axis ticks where vertical space is limited. Returns em-dash for
 * missing or invalid values. Deliberately fixed (not org-driven).
 */
export const formatMonthDay = (value?: string | null): string =>
  formatFixedDateTime(value, DAY_MONTH_FORMAT);

/**
 * Safely formats a date string or `Date`. Handles null, undefined, and invalid
 * dates by returning a fallback string.
 * @param date - The date to format (string, Date, null, or undefined)
 * @param formatStr - The desired output format (defaults to `DATE_FORMAT`)
 * @param fallback - Returned when the date is invalid or missing (defaults to `"—"`)
 */
export const formatDateSafe = (
  date: string | Date | null | undefined,
  formatStr: string = DATE_FORMAT,
  fallback: string = "—",
): string => {
  if (!date) return fallback;
  try {
    const dateObj = typeof date === "string" ? parseISO(date) : date;
    if (!isValid(dateObj)) return fallback;
    return format(dateObj, formatStr);
  } catch {
    return fallback;
  }
};

/** Returns the ordinal suffix for a day of month (`st`, `nd`, `rd`, `th`). */
const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

/**
 * Formats a date string or `Date` to a format like `31st Aug 2025`.
 * Handles date-only strings (`yyyy-MM-dd`) as local dates to avoid timezone shifts.
 * @param date - Date string (`yyyy-MM-dd`) or `Date`
 * @returns Formatted date string with ordinal suffix
 */
export const formatDateWithOrdinal = (date: string | Date | null | undefined): string => {
  if (!date) return "Not specified";

  let dateObj: Date;
  if (typeof date === "string") {
    if (isDateOnlyString(date)) {
      const [year, month, day] = date.split("-").map(Number);
      dateObj = new Date(year, month - 1, day);
    } else {
      dateObj = parseISO(date);
    }
  } else {
    dateObj = date;
  }

  if (!isValid(dateObj)) return "Invalid date";

  const day = dateObj.getDate();
  const month = format(dateObj, "MMM");
  const year = dateObj.getFullYear();
  return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
};

/**
 * Formats a date as a relative time string (e.g. `2h ago`, `Just now`).
 * Optimised for short labels in dropdowns; anything older than a week falls
 * back to the standard date format.
 * @param date - ISO string or `Date`
 */
export const formatTimeAgo = (date: string | Date | null | undefined): string => {
  if (!date) return "";

  const dateObj = typeof date === "string" ? parseISO(date) : date;
  if (!isValid(dateObj)) return "";

  const diffMinutes = Math.floor((new Date().getTime() - dateObj.getTime()) / MS_PER_MINUTE);
  // A week or older falls back to the plain ISO date (this helper's fixed contract);
  // the org-aware sibling `formatRelativeTime` falls back to the org datetime instead.
  return relativeTimePhrase(diffMinutes) ?? formatDate(dateObj);
};
